import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Layout } from '../components/Layout';
import { webchatAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Inbox, MessageCircle, Send, User, Clock, X, Loader2, RefreshCw,
  Phone, Mail, FileText, Globe, UserPlus, GitBranch, Bell, BellOff, Volume2, VolumeX
} from 'lucide-react';
import { WebsiteChatNav } from '../components/WebsiteChatNav';

const STATUS_TABS = [
  { value: 'waiting', label: 'Waiting' },
  { value: 'active', label: 'Active' },
  { value: 'closed', label: 'Closed' },
];

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatResponseTime(seconds) {
  if (seconds == null) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

export default function WebsiteChatInbox() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('waiting');
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [inboxStats, setInboxStats] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => localStorage.getItem('sl_chat_notifications') !== 'off');
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem('sl_chat_sound') !== 'off');
  const [notifPermission, setNotifPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'denied');
  const lastPollTimeRef = useRef(new Date().toISOString());
  const audioRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Create audio element for notification sound
  useEffect(() => {
    audioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YVoGAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAf3hxamVhYGJka3N7goyTl5iWkY2IhIF/f4CBgoKBgH59fHx9f4KHjJKYnJ6dnJmVkYyHg4B/f4CAgYKCgYB/fXx7fH6ChY2TmZ2fnpyZlZGMiISBf3+AgYKCgYGAfn18fH5/g4eLkZaanJybmJWRjIiEgX+AgIGCgoKBgH59fHx8f4KGi5GXm52enJqWkYuHg4B/f4CAgoKCgYF/fn19fX+Ch4uRl5udnpyalZCLh4OAf3+AgYKCgoGAf359fX2Ag4eMkpebnZ6cm5eUj4uHg4CAf4CBgoKCgYB/fn19foGFiY+UmJudnZuZlZGMiISDgYCAgoKCgoGAfn19fX+DhoyRlpqcnZ2bmJSQi4eDgYB/gIGCgoKBgH9+fX5/goaLkJWZm52dnJmVkIuHhIGAgICBgoKCgYB/fn1+gIOHjJGWmpydnZuZlZGMiISBgICAgoODgoGAf35+f4GFiY6Tl5udnZybmJSTj4uIhIKBgYGCgoKBgH9/f4CDh4uQlZmbnZyam5iUkY2JhoOBgIGCgoKCgYCAf4CDhouQlJibnZ2cm5iVkY2JhoOBgIGCgoKCgX9/fn+ChomOk5eanZ2dm5mWkpCMiYaDAA==');
    audioRef.current.volume = 0.3;
  }, []);

  // Request notification permission
  const requestNotifPermission = async () => {
    if (typeof Notification === 'undefined') return;
    const result = await Notification.requestPermission();
    setNotifPermission(result);
  };

  const toggleNotifications = () => {
    const next = !notificationsEnabled;
    setNotificationsEnabled(next);
    localStorage.setItem('sl_chat_notifications', next ? 'on' : 'off');
    if (next && notifPermission !== 'granted') {
      requestNotifPermission();
    }
  };

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    localStorage.setItem('sl_chat_sound', next ? 'on' : 'off');
  };

  // Fire notification
  const fireNotification = useCallback((title, body) => {
    if (notificationsEnabled && notifPermission === 'granted') {
      try { new Notification(title, { body, icon: '/favicon.ico', tag: 'webchat' }); } catch {}
    }
    if (soundEnabled && audioRef.current) {
      try { audioRef.current.play().catch(() => {}); } catch {}
    }
  }, [notificationsEnabled, notifPermission, soundEnabled]);

  // Notification polling
  useEffect(() => {
    if (!notificationsEnabled && !soundEnabled) return;

    const poll = async () => {
      try {
        const res = await webchatAPI.getNotifications(lastPollTimeRef.current);
        const data = res.data;
        if (data.new_messages && data.new_messages.length > 0) {
          const latest = data.new_messages[data.new_messages.length - 1];
          fireNotification(
            `New message from ${latest.visitor_name}`,
            latest.body
          );
          lastPollTimeRef.current = latest.created_at;
        }
      } catch {}
    };

    const interval = setInterval(poll, 6000);
    return () => clearInterval(interval);
  }, [notificationsEnabled, soundEnabled, fireNotification]);

  // Load pipeline stages once
  useEffect(() => {
    webchatAPI.getPipelineStages()
      .then(res => setPipelineStages(res.data))
      .catch(() => {});
  }, []);

  // Load inbox stats (and refresh periodically)
  const loadStats = useCallback(async () => {
    try {
      const res = await webchatAPI.getStats();
      setInboxStats(res.data);
    } catch {}
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    const interval = setInterval(loadStats, 15000);
    return () => clearInterval(interval);
  }, [loadStats]);

  const loadSessions = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const res = await webchatAPI.listSessions({ status: activeTab, limit: 50 });
      setSessions(Array.isArray(res.data) ? res.data : []);
    } catch {
      // Silently fail — don't toast on polling failures
      if (showRefresh) toast.error('Failed to refresh sessions');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [activeTab]);

  useEffect(() => {
    setLoading(true);
    setSessions([]);
    loadSessions();
  }, [loadSessions]);

  // Auto-refresh every 10 seconds (silent)
  useEffect(() => {
    const interval = setInterval(() => loadSessions(), 10000);
    return () => clearInterval(interval);
  }, [loadSessions]);

  const loadSessionDetail = useCallback(async (sessionId) => {
    setDetailLoading(true);
    try {
      const res = await webchatAPI.getSession(sessionId);
      setSessionDetail(res.data);
    } catch {
      // Only toast on first load, not on poll
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedSession) {
      loadSessionDetail(selectedSession);
    } else {
      setSessionDetail(null);
    }
  }, [selectedSession, loadSessionDetail]);

  // Auto-scroll messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [sessionDetail?.messages]);

  // Poll for new messages in active session
  useEffect(() => {
    if (!selectedSession) return;
    const interval = setInterval(() => loadSessionDetail(selectedSession), 5000);
    return () => clearInterval(interval);
  }, [selectedSession, loadSessionDetail]);

  const handleReply = async () => {
    if (!replyText.trim() || !selectedSession) return;
    setSending(true);
    try {
      await webchatAPI.replyToSession(selectedSession, replyText.trim());
      setReplyText('');
      await loadSessionDetail(selectedSession);
      await loadSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleClose = async (sessionId) => {
    try {
      await webchatAPI.closeSession(sessionId);
      toast.success('Session closed');
      await loadSessions();
      if (selectedSession === sessionId) {
        setSelectedSession(null);
        setSessionDetail(null);
      }
    } catch {
      toast.error('Failed to close session');
    }
  };

  const handleCreateLead = async (sessionId, stageId) => {
    try {
      const res = await webchatAPI.createLead(sessionId, stageId);
      toast.success(`Lead created: ${res.data.customer_name}`);
      await loadSessionDetail(sessionId);
      await loadSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create lead');
    }
  };

  const handleAssignPipeline = async (sessionId, stageId) => {
    try {
      const res = await webchatAPI.assignPipeline(sessionId, stageId);
      toast.success(`Assigned to pipeline (${res.data.action})`);
      await loadSessionDetail(sessionId);
      await loadSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign');
    }
  };

  const unreadCount = sessions.filter(s => !s.staff_responded && s.status !== 'closed').length;

  return (
    <Layout>
      <WebsiteChatNav />
      <div className="flex flex-col h-[calc(100vh-64px-41px)]" data-testid="chat-inbox-page">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-background shrink-0">
          <div className="flex items-center gap-2">
            <Inbox className="w-5 h-5 text-blue-600" />
            <h1 className="text-lg font-semibold">Chat Inbox</h1>
            {unreadCount > 0 && (
              <Badge variant="destructive" className="text-xs" data-testid="unread-badge">
                {unreadCount} unread
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleNotifications}
              className={notificationsEnabled ? 'text-blue-600' : 'text-muted-foreground'}
              title={notificationsEnabled ? 'Notifications on' : 'Notifications off'}
              data-testid="notif-toggle-btn"
            >
              {notificationsEnabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleSound}
              className={soundEnabled ? 'text-blue-600' : 'text-muted-foreground'}
              title={soundEnabled ? 'Sound on' : 'Sound off'}
              data-testid="sound-toggle-btn"
            >
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => loadSessions(true)}
              disabled={refreshing}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* SESSION LIST (left panel) */}
          <div className="w-80 border-r flex flex-col shrink-0 bg-muted/30" data-testid="session-list-panel">
            {/* Compact summary row */}
            {inboxStats && (
              <div className="px-3 py-2 border-b bg-background flex items-center gap-4 text-xs shrink-0" data-testid="inbox-summary-row">
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground">Today</span>
                  <span className="font-semibold">{inboxStats.chats_today}</span>
                </div>
                <div className="w-px h-3.5 bg-border" />
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground">Waiting</span>
                  <span className={`font-semibold ${inboxStats.active_sessions > 0 ? 'text-amber-600' : ''}`}>{inboxStats.active_sessions}</span>
                </div>
                <div className="w-px h-3.5 bg-border" />
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground">Resp</span>
                  <span className="font-semibold">{inboxStats.avg_first_response_seconds != null ? formatResponseTime(inboxStats.avg_first_response_seconds) : '—'}</span>
                </div>
                <div className="w-px h-3.5 bg-border" />
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground">Conv</span>
                  <span className="font-semibold">{inboxStats.conversion_today}%</span>
                </div>
              </div>
            )}
            <div className="p-2 border-b">
              <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSelectedSession(null); setSessionDetail(null); }}>
                <TabsList className="w-full">
                  {STATUS_TABS.map(tab => (
                    <TabsTrigger key={tab.value} value={tab.value} className="flex-1 text-xs" data-testid={`tab-${tab.value}`}>
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>

            <div className="flex-1 overflow-y-auto" data-testid="session-list">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : sessions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center" data-testid="empty-state">
                  <MessageCircle className="w-8 h-8 text-muted-foreground/30 mb-2" />
                  <p className="text-sm text-muted-foreground">No {activeTab} chats</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">
                    {activeTab === 'waiting' ? 'New visitor chats will appear here' : activeTab === 'active' ? 'Chats you\'re responding to' : 'Past conversations'}
                  </p>
                </div>
              ) : (
                sessions.map(session => (
                  <SessionRow
                    key={session.id}
                    session={session}
                    selected={selectedSession === session.id}
                    onClick={() => setSelectedSession(session.id)}
                  />
                ))
              )}
            </div>
          </div>

          {/* CONVERSATION PANEL (right) */}
          <div className="flex-1 flex flex-col min-w-0" data-testid="conversation-panel">
            {!selectedSession ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <MessageCircle className="w-10 h-10 mx-auto mb-3 opacity-20" />
                  <p className="text-sm">Select a conversation to view</p>
                </div>
              </div>
            ) : detailLoading && !sessionDetail ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : sessionDetail ? (
              <>
                {/* Conversation header */}
                <div className="flex items-center justify-between px-4 py-3 border-b bg-background shrink-0" data-testid="conversation-header">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                      <User className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate" data-testid="visitor-name">
                        {sessionDetail.intake_first_name
                          ? `${sessionDetail.intake_first_name} ${sessionDetail.intake_last_name || ''}`.trim()
                          : 'Visitor'}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {sessionDetail.origin_domain && (
                          <span className="flex items-center gap-1">
                            <Globe className="w-3 h-3" />
                            {sessionDetail.origin_domain}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {timeAgo(sessionDetail.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={sessionDetail.status} />
                    {sessionDetail.status !== 'closed' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleClose(sessionDetail.id)}
                        className="text-muted-foreground hover:text-red-600"
                        data-testid="close-session-btn"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Quick Actions toolbar */}
                {sessionDetail.status !== 'closed' && (
                  <QuickActions
                    session={sessionDetail}
                    stages={pipelineStages}
                    onCreateLead={handleCreateLead}
                    onAssignPipeline={handleAssignPipeline}
                  />
                )}

                {/* Intake card */}
                {sessionDetail.intake_submitted_at && (
                  <div className="mx-4 mt-3 p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm shrink-0" data-testid="intake-card">
                    <p className="font-medium text-blue-800 mb-1.5 flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5" /> Lead Information
                    </p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-blue-900">
                      {sessionDetail.intake_first_name && (
                        <p><span className="text-blue-600 text-xs">Name:</span> {sessionDetail.intake_first_name} {sessionDetail.intake_last_name || ''}</p>
                      )}
                      {sessionDetail.intake_email && (
                        <p className="flex items-center gap-1"><Mail className="w-3 h-3 text-blue-500" /> {sessionDetail.intake_email}</p>
                      )}
                      {sessionDetail.intake_phone && (
                        <p className="flex items-center gap-1"><Phone className="w-3 h-3 text-blue-500" /> {sessionDetail.intake_phone}</p>
                      )}
                    </div>
                    {sessionDetail.intake_description && (
                      <p className="mt-1.5 text-blue-800 bg-blue-100/60 rounded px-2 py-1 text-xs">{sessionDetail.intake_description}</p>
                    )}
                  </div>
                )}

                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="messages-thread">
                  {sessionDetail.messages?.map(msg => (
                    <MessageBubble key={msg.id} message={msg} />
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply bar */}
                {sessionDetail.status !== 'closed' && (
                  <div className="px-4 py-3 border-t bg-background shrink-0" data-testid="reply-bar">
                    <div className="flex gap-2">
                      <Input
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleReply()}
                        placeholder="Type a reply..."
                        className="flex-1"
                        disabled={sending}
                        data-testid="reply-input"
                      />
                      <Button
                        onClick={handleReply}
                        disabled={sending || !replyText.trim()}
                        size="sm"
                        data-testid="send-reply-btn"
                      >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </Layout>
  );
}

// ─── Quick Actions Toolbar ───
function QuickActions({ session, stages, onCreateLead, onAssignPipeline }) {
  const [creatingLead, setCreatingLead] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [showAssignSelect, setShowAssignSelect] = useState(false);

  const hasCustomer = !!session.customer_id;
  const hasPipelineRecord = !!session.pipeline_record_id;
  const hasIntake = !!session.intake_submitted_at;

  const handleCreateLead = async () => {
    setCreatingLead(true);
    try {
      await onCreateLead(session.id, null);
    } finally {
      setCreatingLead(false);
    }
  };

  const handleAssign = async (stageId) => {
    setAssigning(true);
    setShowAssignSelect(false);
    try {
      await onAssignPipeline(session.id, stageId);
    } finally {
      setAssigning(false);
    }
  };

  const openStages = stages.filter(s => s.stage_type === 'open');

  return (
    <div className="px-4 py-2 border-b bg-muted/20 flex items-center gap-2 flex-wrap shrink-0" data-testid="quick-actions">
      {/* Create Lead */}
      {!hasCustomer && hasIntake && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleCreateLead}
          disabled={creatingLead}
          className="text-xs h-7 gap-1.5"
          data-testid="create-lead-btn"
        >
          {creatingLead ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
          Create Lead
        </Button>
      )}

      {hasCustomer && !hasPipelineRecord && (
        <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-200">
          Lead created
        </Badge>
      )}

      {/* Assign to Pipeline */}
      {!showAssignSelect ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAssignSelect(true)}
          disabled={assigning || openStages.length === 0}
          className="text-xs h-7 gap-1.5"
          data-testid="assign-pipeline-btn"
        >
          {assigning ? <Loader2 className="w-3 h-3 animate-spin" /> : <GitBranch className="w-3 h-3" />}
          {hasPipelineRecord ? 'Move Stage' : 'Assign to Pipeline'}
        </Button>
      ) : (
        <div className="flex items-center gap-1.5" data-testid="stage-select-group">
          <Select onValueChange={handleAssign}>
            <SelectTrigger className="h-7 text-xs w-[180px]" data-testid="stage-select">
              <SelectValue placeholder="Select stage..." />
            </SelectTrigger>
            <SelectContent>
              {openStages.map(stage => (
                <SelectItem key={stage.id} value={stage.id}>
                  <span className="flex items-center gap-1.5">
                    {stage.color && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: stage.color }} />}
                    {stage.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => setShowAssignSelect(false)}
          >
            <X className="w-3 h-3" />
          </Button>
        </div>
      )}

      {hasPipelineRecord && (
        <Badge variant="outline" className="text-xs text-blue-600 border-blue-200" data-testid="pipeline-badge">
          In Pipeline
        </Badge>
      )}
    </div>
  );
}

// ─── Session Row ───
function SessionRow({ session, selected, onClick }) {
  const name = session.intake_first_name
    ? `${session.intake_first_name} ${session.intake_last_name || ''}`.trim()
    : 'Visitor';
  const isUnread = !session.staff_responded && session.status !== 'closed';

  return (
    <button
      className={`w-full text-left px-3 py-3 border-b transition-colors ${
        selected ? 'bg-blue-50 border-l-2 border-l-blue-600' : 'hover:bg-muted/50'
      }`}
      onClick={onClick}
      data-testid={`session-row-${session.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className={`text-sm truncate ${isUnread ? 'font-semibold' : 'font-medium'}`}>{name}</p>
            {isUnread && <span className="w-2 h-2 bg-blue-600 rounded-full shrink-0" />}
          </div>
          {session.origin_domain && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">{session.origin_domain}</p>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-muted-foreground">{timeAgo(session.last_message_at || session.created_at)}</p>
          <div className="flex items-center justify-end gap-1 mt-1">
            <StatusBadge status={session.status} small />
            {session.intake_submitted_at && (
              <Badge variant="outline" className="text-[10px] px-1 py-0">Lead</Badge>
            )}
            {session.pipeline_record_id && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 border-blue-200 text-blue-600">CRM</Badge>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

// ─── Status Badge ───
function StatusBadge({ status, small }) {
  const styles = {
    waiting: 'bg-amber-100 text-amber-700 border-amber-200',
    active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    closed: 'bg-zinc-100 text-zinc-500 border-zinc-200',
    abandoned: 'bg-red-100 text-red-600 border-red-200',
    intake: 'bg-blue-100 text-blue-700 border-blue-200',
  };

  return (
    <span className={`inline-flex items-center rounded-full border px-1.5 ${small ? 'text-[10px] py-0' : 'text-xs py-0.5'} ${styles[status] || styles.waiting}`} data-testid={`status-badge-${status}`}>
      {status}
    </span>
  );
}

// ─── Message Bubble ───
function MessageBubble({ message }) {
  const isVisitor = message.sender_type === 'visitor';
  const isSystem = message.sender_type === 'system';

  if (isSystem) {
    return (
      <div className="text-center" data-testid={`msg-${message.id}`}>
        <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">
          {message.body}
        </span>
      </div>
    );
  }

  return (
    <div className={`flex ${isVisitor ? 'justify-start' : 'justify-end'}`} data-testid={`msg-${message.id}`}>
      <div className={`max-w-[70%] px-3 py-2 rounded-xl text-sm ${
        isVisitor
          ? 'bg-muted rounded-bl-sm'
          : 'bg-blue-600 text-white rounded-br-sm'
      }`}>
        <p className="whitespace-pre-wrap break-words">{message.body}</p>
        <p className={`text-[10px] mt-1 ${isVisitor ? 'text-muted-foreground' : 'text-blue-200'}`}>
          {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
    </div>
  );
}
